import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UiLanguageService } from 'projects/borrower-app/src/app/services/ui-language.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PortalScope } from '../../login/login.component';
import { ApplicationForUser } from '../../models/borrower-portal/application-for-user-model';
import { ContactInfoModel } from '../../models/borrower-portal/contact-info-model';
import { BasePortalService } from '../../services/base-portal.service';
import { DrawerOptions, DrawerSize } from '../../services/drawer.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NotificationService } from '../../services/notification.service';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { ApplicationTaskModel, BorrowerTaskType } from '../../models/borrower-portal/application-task-model';
import { PortalTaskInformationComponent, TasksLoadedEvent } from '../task-information/task-information.component';
import { LoanActions } from '../loan-actions/loan-actions.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { PortalContent } from '../../models/borrower-portal/portal-content-model-model';
import { PaymentLinkConfig } from '../../models/borrower-portal/payment-link-config.model';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OtherBorrowersOnLoanModel } from '../../models/borrower-portal/other-borrowers-on-loan-model';
import { SignalRService } from '../../services/signalr.service';

@Component({
    selector: 'loan-details',
    templateUrl: './loan-details.component.html',
    styleUrls: ['./loan-details.component.scss'],
    standalone: false
})
export class PortalLoanDetailsComponent implements OnInit, OnDestroy {

  private readonly _uiLanguage = inject(UiLanguageService);

  @ViewChild('tasksView')
  tasksView: PortalTaskInformationComponent;

  loanId: number;
  application: ApplicationForUser;

  agentRequestACallDrawerOptions: DrawerOptions = {
    size: DrawerSize.Large,
    containerWrapperId: null
  }

  borrowerContact: ContactInfoModel;

  agentAddressHidden: boolean = true;
  isAgent: boolean = false;
  portalContent: PortalContent | null = null;

  otherBorrowersOnLoan: OtherBorrowersOnLoanModel[] = []

  protected actionsApplicableToLoan: LoanActions;

  protected paymentLinkConfig: PaymentLinkConfig = {
    isVisible: false,
    paymentLink: null,
    buttonText: null,
  }

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _portalService: BasePortalService,
    private readonly _router: Router,
    private readonly _localStorage: LocalStorageService,
    private readonly _notifsService: NotificationService,
    private readonly _environment: EnvironmentService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _signalRService: SignalRService) {

    let userScopeFromPath = null;
    if (this._activatedRoute.routeConfig && this._activatedRoute.routeConfig.data && this._activatedRoute.routeConfig.data.scope) {
      userScopeFromPath = this._activatedRoute.routeConfig.data.scope;
    }

    const userScopeFromLocalStorage = this._localStorage.authorizationData.scope as PortalScope;

    const userScope = this._environment.scope || userScopeFromPath || userScopeFromLocalStorage;

    if (userScope && userScope == PortalScope.Agent) {
      this.isAgent = true;
    }
  }

  ngOnInit(): void {
    // Initialise the SignalR connection for real-time updates
    this._signalRService.connect();

    this._route.paramMap.subscribe((params: ParamMap) => {
      // Unregister from previous loan if navigating between loans
      if (this.loanId) {
        this._signalRService.unregisterForLoanEvents(this.loanId);
      }

      this.loanId = +params.get('loanId');

      // LLS-886: borrowers see (and save) their display language for this loan; agents never do.
      if (!this.isAgent) {
        this._uiLanguage.setApplication(this.loanId);
      }

      // Register for loan-specific SignalR events
      this._signalRService.registerForLoanEvents(this.loanId);

      this._portalService.getPortalContent().subscribe({
        next: (portalContent) => {
          this.portalContent = portalContent;
          this.loadApplicationDetails(this.loanId);
          this.loadBorrowerContactInfo(this.loanId);
        }
      });
    });

    // Silently reload tasks when a task is rejected by validation
    this._taskRejectionSub = this._signalRService.taskRejectedByValidation$.pipe(
      filter(event => !!this.loanId && event.applicationId === this.loanId)
    ).subscribe(() => {
      this._bulkUploadRefreshing = true;
      this.tasksView?.reloadTasks(false, true);
    });
  }

  ngOnDestroy(): void {
    this._taskRejectionSub?.unsubscribe();
    if (this.loanId) {
      this._signalRService.unregisterForLoanEvents(this.loanId);
    }
  }

  protected onOpenAssetVerificationClicked = () => {
    this.tasksView?.openDigitalVerificationDialog(BorrowerTaskType.DigitalAssetVerification);
  }

  protected onOpenIncomeVerificationClicked = () => {
    this.tasksView?.openDigitalVerificationDialog(BorrowerTaskType.DigitalIncomeEmploymentVerification);
  }

  protected outstandingTaskCount: number = 0;

  protected onTasksLoaded = async (event: TasksLoadedEvent) => {
    this.outstandingTaskCount = event.requested?.length || 0;
    const silent = this._bulkUploadRefreshing;
    this._bulkUploadRefreshing = false;
    await this.setupActionsAppropriateForLoan(event.all, silent);
  }

  protected onAdditionalDocumentsAddedToLoan = () => {
    this.tasksView.reloadTasks(true);
  }

  private _bulkUploadRefreshing = false;
  private _taskRejectionSub: Subscription;

  protected onBulkUploadConfirmed = () => {
    this._bulkUploadRefreshing = true;
    this.tasksView.reloadTasks(true, true);
  }

  loadApplicationDetails = (appId: number) => {
    this._portalService.getLoanDataForAppId(appId).subscribe({
      next: (application) => {
        this.application = { ...application };
        this.otherBorrowersOnLoan = application.otherBorrowersOnLoan.filter(o=>o.isPortalMerged);
        this._cdr.detectChanges();
      }, error: (err) => {
        const errorMessage = err?.message || "An error occurred loading loan data";
        this._notifsService.showError(errorMessage, "Error");
        this._cdr.detectChanges();
      }
    })
  }

  loadBorrowerContactInfo = (appId: number) => {
    this._portalService.getContactInfoForApplication(appId).subscribe(borrowerContactInfo => {
      this.borrowerContact = borrowerContactInfo;
      this._cdr.detectChanges();
    });
  }

  openAgentContact = () => {
    this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/request-a-call`], { queryParams: { loanId: this.loanId } });
  }

  protected mobileActiveTab: string = 'tasks';

  protected onMobileNavClicked = (section: string) => {
    this.mobileActiveTab = section;
  }

  protected get shouldShowLoanActions(): boolean {
    return this.actionsApplicableToLoan?.hasAssetVerificationTasks ||
      this.actionsApplicableToLoan?.hasIncomeVerificationTasks ||
      this.actionsApplicableToLoan?.isMortgageCalculatorEnabled ||
      this.actionsApplicableToLoan?.isUploadingAdditionalDocsEnabled ||
      this.actionsApplicableToLoan?.hasPreApprovalLetter ||
      this.actionsApplicableToLoan?.hasPaymentLink ||
      false;
  }

  private setupActionsAppropriateForLoan = async (allTasks: ApplicationTaskModel[], silent: boolean = false) => {

    let actionsAppropriateForLoan = new LoanActions();

    let hasIncomeVerificationTasks = false;
    let hasAssetVerificationTasks = false;
    if (!this.isAgent) {
      hasAssetVerificationTasks = allTasks.some(task => task.borrowerTaskType == BorrowerTaskType.DigitalAssetVerification);
      hasIncomeVerificationTasks = allTasks.some(task => task.borrowerTaskType == BorrowerTaskType.DigitalIncomeEmploymentVerification);
    }

    actionsAppropriateForLoan.isMortgageCalculatorEnabled = this.application.companyId != 252;

    try {
      actionsAppropriateForLoan.hasAssetVerificationTasks = this.application.isAssetVerificationLoanActionEnabled && hasAssetVerificationTasks;
      actionsAppropriateForLoan.hasIncomeVerificationTasks = this.application.isEmploymentIncomeVerificationLoanActionEnabled && hasIncomeVerificationTasks;
      actionsAppropriateForLoan.isMortgageCalculatorEnabled = !!this.application.isMortgageCalculatorLoanActionEnabled;
      actionsAppropriateForLoan.isUploadingAdditionalDocsEnabled = !!this.application.isUploadingAdditionalDocumentsLoanActionEnabled;
      actionsAppropriateForLoan.hasPreApprovalLetter = !!this.application.isPreApprovalLetterGenerationLoanActionEnabled;

      if (!silent) {
        this.paymentLinkConfig = await this.loadPaymentLinkConfig(this.application.applicationId);
      }

      if (this.paymentLinkConfig && this.paymentLinkConfig.isVisible) {
        actionsAppropriateForLoan.hasPaymentLink = true;
      }

      if (Object.values(actionsAppropriateForLoan).every(value => value === false)) {
        actionsAppropriateForLoan = null;
      }

      this.actionsApplicableToLoan = actionsAppropriateForLoan;

    } catch (error) {
      if (error.error !== 'Prequal detail was not found') {
        this._notifsService.showError("An error occurred loading loan actions", "Error");
      }
    }
    this._spinner.hide();
    this._cdr.detectChanges();
  }

  private loadPaymentLinkConfig = async (appId: number): Promise<PaymentLinkConfig> => {
      try {
       this._spinner.show();
       const paymentLinkConfig = await firstValueFrom(
          this._portalService.getPaymentLinkConfig(appId)
        );
        return paymentLinkConfig;
      } catch (error) {
        return {
          isVisible: false,
          paymentLink: null,
          buttonText: null,
        };
      } finally {
        this._spinner.hide();
      }
    }
}
